import { resolveTermApiBase } from '@1delta/data-sdk'
import type { TermMarketConfig } from '@1delta/data-sdk'
import type {
  TermAuctionOrder,
  TermAuctionOrders,
  TermAuctionWindow,
  TermBook,
  TermBookLevel,
  TermBookSource,
  TermBookTop,
  TermListing,
} from './types'

/**
 * Term Finance public-data source — the Ormi-hosted GraphQL subgraph
 * (`term-finance-<chain>`). Provides the live secondary-market orders
 * (`termOrder` = RepoTokenLinkedList listings) and the latest auction clearing
 * price per repo, which we reduce to a fixed lend/borrow APR + depth.
 *
 * Base URL resolves as: runtime override / config `apiBaseUrl`
 * (`resolveTermApiBase`) → the per-chain default below → '' (no source).
 */

const WAD = 1e18

/** Default Ormi core subgraph endpoints (org 05e9a4e2-…) by chainId. */
export const TERM_SUBGRAPH_BY_CHAIN: Record<string, string> = {
  '1': 'https://api.subgraph.ormilabs.com/api/public/05e9a4e2-103b-4163-a81e-3b1b038d0055/subgraphs/term-finance-mainnet/latest/gn',
  '43114':
    'https://api.subgraph.ormilabs.com/api/public/05e9a4e2-103b-4163-a81e-3b1b038d0055/subgraphs/term-finance-avalanche/latest/gn',
  '56': 'https://api.subgraph.ormilabs.com/api/public/05e9a4e2-103b-4163-a81e-3b1b038d0055/subgraphs/term-finance-bnb/latest/gn',
  '8453':
    'https://api.subgraph.ormilabs.com/api/public/05e9a4e2-103b-4163-a81e-3b1b038d0055/subgraphs/term-finance-base/latest/gn',
  '42161':
    'https://api.subgraph.ormilabs.com/api/public/05e9a4e2-103b-4163-a81e-3b1b038d0055/subgraphs/term-finance-arbitrum/latest/gn',
}

const LENDING_DEBUG = false
const warn = (...args: unknown[]) => {
  if (LENDING_DEBUG) console.warn('[term-subgraph]', ...args)
}

type FetchLike = typeof fetch

/** Resolve a chain's Term subgraph URL (override/config → per-chain default → ''). */
export function termApiBaseUrl(chainId: string): string {
  return resolveTermApiBase(chainId) || TERM_SUBGRAPH_BY_CHAIN[chainId] || ''
}

const toNum = (v: unknown): number => {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
const toBig = (v: unknown): bigint => {
  try {
    return v == null ? 0n : BigInt(String(v))
  } catch {
    return 0n
  }
}

/**
 * Reduce the repo's not-yet-complete auction rounds to the ONE that is really
 * live (or about to be).
 *
 * `auctionComplete: false` alone is not enough: the subgraph carries a long
 * tail of rounds that were listed, never cleared, and left behind — on Ethereum
 * most repos have several, some over two years old. Two filters cut them:
 *  - `delisted` — Term's own marker for a retired listing.
 *  - a clearing time already in the past — a round that should have completed
 *    and didn't is abandoned, whatever its flags say.
 *
 * Of what survives, the round clearing SOONEST is the current one (later ones,
 * if any, are future rounds on the same repo).
 */
export function pickAuction(
  rows: any[] | null | undefined,
  nowSec: number = Math.floor(Date.now() / 1000),
): TermAuctionWindow | null {
  if (!Array.isArray(rows) || rows.length === 0) return null
  const live = rows
    .filter((a) => a && !a.delisted && !a.nonViableAuction)
    .map((a) => ({
      id: String(a.id ?? ''),
      startTime: toNum(a.auctionStartTime),
      revealTime: toNum(a.revealTime),
      endTime: toNum(a.auctionEndTime),
      minBidAmount: String(a.auctionMinBidAmount ?? '0'),
      minOfferAmount: String(a.auctionMinOfferAmount ?? '0'),
      maxBidPriceWad: String(a.auctionMaxBidPrice ?? '0'),
      maxOfferPriceWad: String(a.auctionMaxOfferPrice ?? '0'),
    }))
    .filter((a) => a.endTime > nowSec)
    .sort((a, b) => a.endTime - b.endTime)
  return live[0] ?? null
}

/**
 * GraphQL subgraph source. `getBookTop` derives the fixed APR from the repo's
 * latest completed auction clearing price and open-order depth; `getListings`
 * returns the open secondary-market repo-token orders.
 */
export class TermSubgraphSource implements TermBookSource {
  private readonly url: string
  private readonly fetchImpl: FetchLike

  constructor(url: string, fetchImpl: FetchLike = fetch) {
    this.url = url
    // Bind through globalThis — calling `this.fetchImpl(url)` sets `this` to the
    // instance, which Cloudflare Workers rejects with "Illegal invocation".
    this.fetchImpl = (input: any, init?: any) =>
      fetchImpl.call(globalThis, input, init)
  }

  private async gql(
    query: string,
    variables: Record<string, unknown>,
  ): Promise<any | null> {
    if (!this.url) return null
    try {
      const res = await this.fetchImpl(this.url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ query, variables }),
      })
      if (!res.ok) {
        warn(`HTTP ${res.status}`)
        return null
      }
      const json: any = await res.json()
      if (json?.errors) {
        warn('graphql errors', json.errors)
        return null
      }
      return json?.data ?? null
    } catch (e) {
      warn('fetch failed', e)
      return null
    }
  }

  async getBookTop(config: TermMarketConfig): Promise<TermBookTop | null> {
    return (await this.getTopAndBook(config))?.top ?? null
  }

  /**
   * ONE query → the aggregate top (best APR + FULL depth) PLUS a bounded book
   * slice (top `maxLevels` open orders per side) PLUS the repo's current
   * auction round. `asks` = orders selling repo tokens (the secondary LEND
   * book); `bids` = the rest (borrow side, usually empty — Term borrow is
   * sealed-bid auction, not a continuous book). Term secondary orders carry no
   * per-order rate, so every level shares the market's clearing APR; the levels
   * expose per-order SIZE for filtering.
   *
   * Two auction reads, deliberately distinct:
   *  - `cleared` — the latest COMPLETE round, whose clearing price IS the
   *    market's fixed APR (and stays the reference rate between auctions).
   *  - `pending` — rounds not yet complete/cancelled. Only one of these is a
   *    real, actionable round; the rest are abandoned listings the subgraph
   *    never marked complete, filtered out below.
   */
  async getTopAndBook(
    config: TermMarketConfig,
    maxLevels = 20,
  ): Promise<{
    top: TermBookTop
    book: TermBook
    auction: TermAuctionWindow | null
  } | null> {
    const id = config.termRepoId.toLowerCase()
    const repoToken = config.repoToken.toLowerCase()
    const data = await this.gql(
      `query Top($id: ID!, $termId: String!, $repoId: Bytes!) {
        termRepo(id: $id) { termRepoTokenRedemptionRatio }
        termAuctions(where: { term: $id, auctionComplete: true }, orderBy: auctionEndTime, orderDirection: desc, first: 1) {
          auctionClearingPrice
          dayCountFractionMantissa
        }
        pending: termAuctions(
          where: { term: $termId, auctionComplete: false, auctionCancelled: false }
          orderBy: auctionEndTime
          orderDirection: desc
          first: 5
        ) {
          id
          auctionStartTime
          revealTime
          auctionEndTime
          delisted
          nonViableAuction
          auctionMinBidAmount
          auctionMinOfferAmount
          auctionMaxBidPrice
          auctionMaxOfferPrice
        }
        termOrders(where: { termRepoId: $repoId, orderCancelled: false }, first: 500) {
          makerToken
          originalOrderAmount
          filledAmount
        }
      }`,
      { id, termId: id, repoId: id },
    )
    if (!data) return null

    const redemptionRatio =
      toBig(data.termRepo?.termRepoTokenRedemptionRatio) || BigInt(WAD)
    const auction = data.termAuctions?.[0]
    // Term's auction clearing price IS the annualized clearing interest rate,
    // WAD-scaled (e.g. 0.09e18 = 9% APR). The repurchase premium over the term
    // is rate × dayCountFractionMantissa, but the quoted/clearing figure is
    // already annualized, so the APR is a direct read.
    const clearingRate = toNum(auction?.auctionClearingPrice)
    const aprPct = clearingRate > 0 ? (clearingRate / WAD) * 100 : 0

    const scale = Math.pow(10, config.loanDecimals)
    const toLoan = (repoUnits: bigint) =>
      Number((repoUnits * redemptionRatio) / BigInt(WAD)) / scale

    // Full depth aggregate + a bounded per-order book slice (largest first).
    // Orders selling repo tokens (makerToken == repoToken) are lend liquidity
    // (asks); the rest is borrow liquidity (bids).
    const orders = (data.termOrders ?? [])
      .map((o: any) => ({
        remaining: toBig(o.originalOrderAmount) - toBig(o.filledAmount),
        isAsk: String(o.makerToken).toLowerCase() === repoToken,
      }))
      .filter((o: { remaining: bigint }) => o.remaining > 0n)
      .sort((a: { remaining: bigint }, b: { remaining: bigint }) =>
        a.remaining < b.remaining ? 1 : a.remaining > b.remaining ? -1 : 0,
      )

    let supplyUnits = 0n
    let borrowUnits = 0n
    const asks: TermBookLevel[] = []
    const bids: TermBookLevel[] = []
    for (const o of orders) {
      const arr = o.isAsk ? asks : bids
      if (o.isAsk) supplyUnits += o.remaining
      else borrowUnits += o.remaining
      if (arr.length < maxLevels) {
        arr.push({
          apr: aprPct,
          units: o.remaining.toString(),
          assets: toLoan(o.remaining),
        })
      }
    }

    const top: TermBookTop = {
      supplyAprPct: aprPct,
      borrowAprPct: aprPct,
      supplyLiquidity: toLoan(supplyUnits),
      borrowLiquidity: toLoan(borrowUnits),
    }
    return { top, book: { bids, asks }, auction: pickAuction(data.pending) }
  }

  async getListings(config: TermMarketConfig): Promise<TermListing[] | null> {
    const id = config.termRepoId.toLowerCase()
    const repoToken = config.repoToken.toLowerCase()
    const data = await this.gql(
      `query Listings($repoId: Bytes!) {
        termOrders(where: { termRepoId: $repoId, orderCancelled: false }, first: 500) {
          id
          maker
          makerToken
          originalOrderAmount
          filledAmount
        }
      }`,
      { repoId: id },
    )
    if (!data) return null
    return (
      (data.termOrders ?? [])
        // Only orders selling repo tokens are secondary LEND listings.
        .filter((o: any) => String(o.makerToken).toLowerCase() === repoToken)
        .map((o: any) => {
          const remaining = toBig(o.originalOrderAmount) - toBig(o.filledAmount)
          return {
            listingId: String(o.id),
            seller: String(o.maker),
            repoToken: config.repoToken,
            amount: remaining.toString(),
          } as TermListing
        })
        .filter((l: TermListing) => toBig(l.amount) > 0n)
    )
  }

  /**
   * A user's primary-auction offers + bids for a repo. Offers/bids link to an
   * `auction`, which links to a `term` (the repo) — so we filter by the
   * submitter AND the nested `auction_.term`. The revealed price is 0 (Bytes
   * `unrevealed*Price` still sealed) until the reveal window; `active` marks
   * submissions whose auction is neither complete nor cancelled.
   */
  async getAuctionOrders(
    config: TermMarketConfig,
    account: string,
  ): Promise<TermAuctionOrders | null> {
    const term = config.termRepoId.toLowerCase()
    const who = account.toLowerCase()
    const data = await this.gql(
      `query AuctionOrders($term: String!, $who: Bytes!) {
        termOffers(first: 500, where: { offeror: $who, auction_: { term: $term } }) {
          id
          amount
          assignedAmount
          locked
          revealedOfferPrice
          auction { id auctionComplete auctionCancelled revealTime auctionEndTime }
        }
        termBids(first: 500, where: { bidder: $who, auction_: { term: $term } }) {
          id
          amount
          assignedAmount
          locked
          revealedBidPrice
          auction { id auctionComplete auctionCancelled revealTime auctionEndTime }
        }
      }`,
      { term, who },
    )
    if (!data) return null

    const scale = Math.pow(10, config.loanDecimals)
    const toOrder = (
      o: any,
      side: 'offer' | 'bid',
      revealedRaw: unknown,
    ): TermAuctionOrder => {
      const a = o.auction ?? {}
      const complete = Boolean(a.auctionComplete)
      const cancelled = Boolean(a.auctionCancelled)
      const revealed = toBig(revealedRaw) > 0n
      return {
        id: String(o.id),
        side,
        auctionId: String(a.id ?? ''),
        account,
        amount: String(o.amount ?? '0'),
        assets: Number(toBig(o.amount)) / scale,
        assignedAmount: String(o.assignedAmount ?? '0'),
        locked: Boolean(o.locked),
        revealed,
        revealedPriceWad: revealed ? String(revealedRaw) : null,
        active: !complete && !cancelled,
        auctionComplete: complete,
        auctionCancelled: cancelled,
        revealTime: toNum(a.revealTime),
        auctionEndTime: toNum(a.auctionEndTime),
      }
    }

    return {
      offers: (data.termOffers ?? []).map((o: any) =>
        toOrder(o, 'offer', o.revealedOfferPrice),
      ),
      bids: (data.termBids ?? []).map((o: any) =>
        toOrder(o, 'bid', o.revealedBidPrice),
      ),
    }
  }
}

/** Default Term public-data source for a chain (subgraph via resolved URL). */
export function createTermBookSource(
  chainId: string,
  fetchImpl: FetchLike = fetch,
): TermBookSource {
  return new TermSubgraphSource(termApiBaseUrl(chainId), fetchImpl)
}
